// ============================================================================
// seanceStatut.js — État logique d'une séance Thémis (partagé)
// ============================================================================
// Déplacé tel quel depuis ThemisInstances.js (04/09/2026) : les pages
// d'instance (Gouvernance) affichent le statut des séances sans charger le
// chunk Thémis de 2 000 lignes. Cinq états qui guident l'admin vers l'action
// la plus utile à ce stade :
//   • "brouillon"  : préparation en cours, pas encore convoquée
//   • "convoquee"  : convocation envoyée, en attente de la séance
//   • "en_cours"   : séance live (session active)
//   • "cloturee"   : séance terminée, PV en attente de signature
//   • "archivee"   : PV signé, dossier clos
// ============================================================================
import { dateISOSeance, typeDeSeance, instanceParSessionType } from "./instancesNav";

export function computeStatutSession(sess, sessionState) {
  if (!sess) return "brouillon";
  if (sessionState === "active") return "en_cours";
  if (sessionState === "history") {
    return sess.pvSigne ? "archivee" : "cloturee";
  }
  // pending : selon convocation (deux orthographes historiques)
  return (sess.convocationSent || sess.convocationEnvoyee) ? "convoquee" : "brouillon";
}

// Le statut DIT ce qui est (pastille du bandeau de la fiche, cartes des pages
// d'instance de la Gouvernance, accueil Gouvernance) ; le geste de l'étape
// est celui du bandeau de la fiche (lot 8 de la grammaire des en-têtes,
// 02/11/2026 : principale de l'étape en cours, R15). Plus d'emoji ni de
// libellé d'action ici (ils doublaient la principale dans le bandeau d'état).
// Les explications (`hint`) gardent leurs mots (PV, ODJ : chapitre 7 du plan,
// rien n'est renommé sans Pierre) ; seul le tiret cadratin devient deux-points.
// Elles sont la phrase d'état du bandeau de la fiche et l'infobulle du statut
// sur les pages d'instance de la Gouvernance (InstanceSeances.js).
export const STATUT_META = {
  brouillon: {
    label: "Brouillon",
    color: "#9CA3AF", bg: "rgba(156,163,175,.12)",
    hint: "En préparation : l'instance n'est pas encore convoquée",
  },
  convoquee: {
    label: "Convoquée",
    color: "#F49800", bg: "rgba(244, 152, 0,.15)",
    hint: "Convocation envoyée : en attente de la séance",
  },
  en_cours: {
    label: "En cours",
    color: "#E20816", bg: "rgba(226, 8, 22,.15)",
    hint: "Séance en direct : émargement, ODJ, scrutins",
  },
  cloturee: {
    label: "Clôturée",
    color: "var(--tx-violet)", bg: "rgba(149,27,129,.15)",
    hint: "Séance terminée : PV en attente de signature",
  },
  archivee: {
    label: "Archivée",
    color: "#6B7280", bg: "rgba(107,114,128,.12)",
    hint: "Dossier clos : PV signé",
  },
};

// ── Le signal « séance ouverte » dit toujours vrai (lot L7, 28/09/2026) ──────
// Incident : « Séance en cours : Assemblée générale du 24 septembre 2026 »
// s'affichait encore le 28 sur l'accueil Gouvernance. La séance n'avait pas
// été clôturée dans Thémis ; sessions/current.session restait posée et
// l'accueil la prenait pour un direct. Règle (synthèse M9) : le signal
// s'éteint à la clôture ; 12 h après l'ouverture sans clôture, il devient
// « non clôturée », immobile, et ne se montre qu'à l'équipe. computeStatutSession
// ne change pas : c'est lui qui propose « Clôturer la séance » à l'équipe.
export const SIGNAL_OUVERTE_MAX_MS = 12 * 3600 * 1000;

/** openedAt en millisecondes : nombre, ISO, Timestamp Firestore, ou texte
 *  « 24/09/2026 14:05:07 » (nowFull d'appShared). NaN si illisible. */
export function horodatageOuverture(v) {
  if (v === null || v === undefined || v === "") return NaN;
  if (typeof v === "number") return v;
  if (typeof v === "object" && typeof v.toMillis === "function") return v.toMillis();
  const s = String(v).trim();
  const fr = /^(\d{2})\/(\d{2})\/(\d{4})\D+(\d{1,2}):(\d{2})/.exec(s);
  if (fr) return new Date(+fr[3], +fr[2] - 1, +fr[1], +fr[4], +fr[5]).getTime();
  const t = Date.parse(s);
  return Number.isNaN(t) ? NaN : t;
}

const jourLocal = (ms) => {
  const d = new Date(ms);
  const z = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};

/**
 * @param session  sessions/current.session (la séance « live »), ou null
 * @returns {{ etat: "aucune" | "ouverte" | "non-cloturee", depuis: number|null }}
 */
export function signalSeance(session, maintenant = Date.now()) {
  if (!session) return { etat: "aucune", depuis: null };
  const t = horodatageOuverture(session.openedAt);
  if (Number.isFinite(t)) {
    return maintenant - t > SIGNAL_OUVERTE_MAX_MS
      ? { etat: "non-cloturee", depuis: t }
      : { etat: "ouverte", depuis: t };
  }
  // Sans heure d'ouverture lisible, la date de la séance tranche.
  const jour = dateISOSeance(session);
  if (jour && jour < jourLocal(maintenant)) return { etat: "non-cloturee", depuis: null };
  return { etat: "ouverte", depuis: null };
}

/** « 14 h 05 » (charte : espaces, pas de deux-points). */
export function heureCourte(ms) {
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  return `${d.getHours()} h ${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Statut AFFICHÉ d'une carte de séance : « En cours » ne survit pas 12 h
 *  sans clôture (il devient « Non clôturée », gris). */
export const STATUT_NON_CLOTUREE = {
  label: "Non clôturée",
  color: "var(--text-sub)", bg: "color-mix(in srgb, var(--text-sub) 14%, transparent)",
  hint: "Séance ouverte il y a plus de 12 heures et jamais clôturée",
  // Quelque chose attend : la clôture (pastille colorée du bandeau de la fiche).
  attente: true,
};

// Le document d'une instance qui ne délibère pas est un COMPTE RENDU (Bureau,
// CODIR… : src/documentSeance.js) : son explication ne dit pas « PV » (recette
// du PV signé, 03/11/2026). Objets figés, une fois : une référence stable.
// La nature se lit dans la table d'INSTANCES_NAV, comme regleDocument (type
// inconnu : compte rendu, DOCUMENT_REPLI), sans importer documentSeance.js :
// ce module-ci est dans le bundle principal (signalSeance, App.jsx), pas lui.
const STATUT_META_CR = Object.freeze({
  cloturee: Object.freeze({ ...STATUT_META.cloturee, hint: "Séance terminée : compte rendu en attente de signature" }),
  archivee: Object.freeze({ ...STATUT_META.archivee, hint: "Dossier clos : compte rendu signé" }),
});

function natureDocument(sess) {
  const inst = instanceParSessionType(typeDeSeance(sess) || "CA");
  return (inst && inst.document && inst.document.nature) || "compte-rendu";
}

export function metaStatutAffiche(sess, sessionState, maintenant = Date.now()) {
  const statut = computeStatutSession(sess, sessionState);
  if (statut === "en_cours" && signalSeance(sess, maintenant).etat === "non-cloturee") return STATUT_NON_CLOTUREE;
  if (STATUT_META_CR[statut] && sess && natureDocument(sess) !== "proces-verbal") return STATUT_META_CR[statut];
  return STATUT_META[statut] || STATUT_META.brouillon;
}
